import { useCallback, useEffect, useState } from "react";
import {
  UsersRound, Clock4, MapPin, ClockFading, CalendarDays, GraduationCap,
  BookUser, UserRound, Briefcase, Globe,
  UserRoundCheck, Coins, UserPen, UserCog, Workflow,
  MailCheck, Loader2,
} from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import RetryButton from "@/components/shared/RetryButton";
import ModuleGate from "@/permissions/ModuleGate";
import { TenantSettingsService } from "@/services/tenantSettingsService";
import { formatDate } from "@/utils/dateUtils";
import { Button } from "@/components/ui/Button";
import { ViewJobSkeleton } from "./LoadingSkeleton";
import { parseArray } from "@/utils/parseArray";
import { toLanguageStrings } from "@/utils/languageRequirements";
import { formatExperienceRange } from "@/utils/experience";
import { EMPLOYEE_TYPE } from "@/config/dropdownData";
import { formatTypeLabel } from "@/pages/admin/candidate-analysis/interview/constants/interviewTypes";

const MOBILE_SKILL_LIMIT = 4;

const hasValue = (val) => {
  if (val === 0) return true;
  if (!val) return false;
  if (Array.isArray(val)) return val.length > 0;
  if (typeof val === "string") return val.replace(/[{}]/g, "").trim().length > 0;
  return true;
};

const getEmployeeTypeLabel = (value) => EMPLOYEE_TYPE.find((item) => item.value === value)?.label || value;

const formatBulletList = (value) => {
  const text = Array.isArray(value) ? value.join("\n") : String(value || "");
  return text
    .split("\n")
    .map((i) => i.trim().replace(/^[•.-]\s*/, ""))
    .filter(Boolean);
};

// ── Reusable section wrapper ────────────────────────────────────────────────
const Section = ({ title, children }) => (
  <div className="flex flex-col gap-3">
    <h3 className="text-[14px] font-semibold uppercase tracking-wider text-text-secondary">{title}</h3>
    {children}
  </div>
);

// ── Shared tile style ───────────────────────────────────────────────────────
const tileBase = "flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-background-icon/20 border border-border-primary/50 min-w-0";
const statTileBase =
  "flex flex-col items-center justify-center text-center gap-1 px-3 py-2.5 rounded-lg bg-background-icon/20 border border-border-primary/50 min-w-0 min-h-[58px]";

const JobDetailsBody = ({ viewJob, viewLoading }) => {
  const [skillsExpanded, setSkillsExpanded] = useState(false);

  const skills = viewJob?.skills ?? [];
  const hiddenSkillCount = skills.length - MOBILE_SKILL_LIMIT;
  const visibleSkills = skillsExpanded || hiddenSkillCount <= 0 ? skills : skills.slice(0, MOBILE_SKILL_LIMIT);

  if (viewLoading) return <ViewJobSkeleton />;
  if (!viewJob) return null;

  const basicItems = [
    {
      icon: MapPin,
      label: "Location",
      value: viewJob.location,
      href: viewJob.location ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(viewJob.location)}` : null,
    },
    { icon: Briefcase, label: "Experience", value: formatExperienceRange(viewJob.experience_min, viewJob.experience_max) },
    { icon: ClockFading, label: "Shift", value: viewJob.shift_timing },
    { icon: CalendarDays, label: "Calendar", value: viewJob.calendar_type_name },
    { icon: UserRound, label: "Employee Type", value: getEmployeeTypeLabel(viewJob.employee_type) },
    { icon: Workflow, label: "Status Flow", value: viewJob.status_flow_name },
    {
      icon: Coins,
      label: "Currency",
      value: viewJob.currency ? `${viewJob.currency_sign ? `${viewJob.currency_sign} ` : ""}${viewJob.currency}` : null,
    },
    { icon: GraduationCap, label: "Department", value: viewJob.department },
    { icon: BookUser, label: "Division", value: viewJob.division },
    { icon: Clock4, label: "Deadline", value: viewJob.deadline ? formatDate(viewJob.deadline) : null },
    { icon: Clock4, label: "Job Posted", value: viewJob.created_at ? formatDate(viewJob.created_at) : null },
    { icon: UsersRound, label: "Openings", value: viewJob.vacancy_count != null ? String(viewJob.vacancy_count) : null },
    { icon: UserRoundCheck, label: "Hired", value: viewJob.hired_count != null ? String(viewJob.hired_count) : null },
    {
      icon: UsersRound,
      label: "Available",
      value: viewJob.remaining_vacancies != null ? String(viewJob.remaining_vacancies) : null,
    },
    {
      icon: UserPen,
      label: "Created By",
      value: viewJob.created_by_details?.name || viewJob.created_by_details?.email,
    },
    {
      icon: UserCog,
      label: "Assigned To",
      value: viewJob.assignments?.[0]?.name || viewJob.assignments?.[0]?.email,
    },
  ].filter(c => hasValue(c.value));

  const paddedItems = basicItems;

  return (
    <div className="flex flex-col gap-6">
      {/* ── Basic Info tiles ─────────────────────────────────────────────── */}
      <Section title="Basic Information">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {paddedItems.map((item, i) => item ? (
            <div key={item.label} className={`${statTileBase} w-full`}>
              <div className="flex gap-1 items-center">
                <item.icon size={10} className="text-text-secondary flex-shrink-0" />
              <p className="text-[10px] text-text-secondary leading-none">{item.label}</p>
              </div>
              <p title={String(item.value)} className={`w-full text-[13px] font-medium leading-none truncate ${item.green ? "text-emerald-400" : "text-text-primary"}`}>
                {item.value}
              </p>
            </div>
          ) : (
            <div key={`empty-${i}`} className="rounded-lg border border-border-primary/20 bg-background-icon/10" />
          ))}
        </div>
      </Section>

      {/* ── Skills ───────────────────────────────────────────────────────── */}
      {skills.length > 0 && (
        <Section title="Required Skills">
          <div className="flex flex-wrap gap-2">
            {/* mobile: limited */}
            <div className="flex flex-wrap gap-2 sm:hidden">
              {visibleSkills.map((skill, i) => (
                <Badge key={skill} label={skill} variant="gradient" colorIndex={i % 6} className="rounded-md border" badgeStyle="px-3 py-2 text-[10px]" />
              ))}
              {!skillsExpanded && hiddenSkillCount > 0 && (
                <Button
                  onClick={() => setSkillsExpanded(true)}
                  className="px-3 py-0.5 text-[10px] rounded-lg border border-border-primary bg-background-icon/20 text-text-secondary hover:bg-background-icon/40 transition-colors"
                >
                  +{hiddenSkillCount} more
                </Button>
              )}
              {skillsExpanded && hiddenSkillCount > 0 && (
                <Button
                  onClick={() => setSkillsExpanded(false)}
                  className="px-3 py-0.5 text-[10px] rounded-lg border border-border-primary bg-background-icon/20 text-text-secondary hover:bg-background-icon/40 transition-colors"
                >
                  Show less
                </Button>
              )}
            </div>
            {/* desktop: all */}
            <div className="hidden sm:flex flex-wrap gap-2">
              {skills.map((skill, i) => (
                <Badge key={skill} label={skill} variant="gradient" colorIndex={i % 6} className="rounded-lg border" badgeStyle="px-4 text-xs" />
              ))}
            </div>
          </div>
        </Section>
      )}

      {/* ── Role Overview ────────────────────────────────────────────────── */}
      {hasValue(viewJob.role_overview) && (
        <Section title="Role Overview">
          <p className="text-[13px] text-text-primary leading-relaxed whitespace-pre-line">{viewJob.role_overview}</p>
        </Section>
      )}

      {/* ── Key Responsibilities ─────────────────────────────────────────── */}
      {hasValue(viewJob.key_responsibilities) && (
        <Section title="Key Responsibilities">
          <ul className="flex flex-col gap-1.5 pl-1">
            {formatBulletList(viewJob.key_responsibilities).map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-[13px] text-text-primary">
                <span className="mt-[6px] w-1 h-1 rounded-full bg-text-secondary flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* ── Nice to Have ─────────────────────────────────────────────────── */}
      {hasValue(viewJob.nice_to_have) && (
        <Section title="Nice to Have">
          <ul className="flex flex-col gap-1.5 pl-1">
            {formatBulletList(viewJob.nice_to_have).map((item) => (
              <li key={item} className="flex items-start gap-2 text-[13px] text-text-primary">
                <span className="mt-[6px] w-1 h-1 rounded-full bg-text-secondary flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* ── Certifications + Languages side by side ──────────────────────── */}
      {(hasValue(viewJob.certifications) || hasValue(viewJob.languages_required)) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {hasValue(viewJob.certifications) && (
            <Section title="Certifications">
              <div className="flex flex-wrap gap-1.5">
                {parseArray(viewJob.certifications)
                  .split(",")
                  .map(
                    (certification) =>
                      certification.trim() && (
                        <span
                          key={certification.trim()}
                          className="text-[12px] px-2.5 py-1 rounded-lg border border-border-primary bg-background-icon/20 text-text-primary"
                        >
                          {certification.trim()}
                        </span>
                      ),
                  )}
              </div>
            </Section>
          )}
          {hasValue(viewJob.languages_required) && (
            <Section title="Languages">
              <div className="flex flex-wrap gap-1.5">
                {toLanguageStrings(viewJob.languages_required)
                  .filter(Boolean)
                  .map((language) => (
                    <span key={language} className="text-[12px] px-2.5 py-1 rounded-lg border border-border-primary bg-background-icon/20 text-text-primary">
                      {language}
                    </span>
                  ))}
              </div>
            </Section>
          )}
        </div>
      )}

      {/* ── Interview Question Types ─────────────────────────────────────── */}
      {hasValue(viewJob.interview_question_types) && (
        <Section title="Interview Question Types">
          <div className="flex flex-wrap gap-1.5">
            {viewJob.interview_question_types.map((type) => (
              <span key={type} className="text-[12px] px-2.5 py-1 rounded-lg border border-border-primary bg-background-icon/20 text-text-primary">
                {formatTypeLabel(type)}
              </span>
            ))}
          </div>
        </Section>
      )}

      {/* ── Required from Candidates ─────────────────────────────────────── */}
      {hasValue(viewJob.mandatory_fields) && (
        <Section title="Required from Candidates">
          {(() => {
            const grouped = viewJob.mandatory_fields.reduce((acc, key) => {
              const parts = key.split(".");
              const category = parts.length > 1 ? parts[0] : "general";
              if (!acc[category]) acc[category] = [];
              acc[category].push(key);
              return acc;
            }, {});
            const formatLabel = (key) =>
              key
                .split(".")
                .pop()
                .toLowerCase()
                .replace(/_/g, " ")
                .replace(/\b\w/g, (l) => l.toUpperCase());
            const formatCategory = (cat) =>
              cat === "general"
                ? "General"
                : cat
                    .toLowerCase()
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase());
            return (
              <div className="flex flex-col gap-3">
                {Object.entries(grouped).map(([category, keys]) => (
                  <div key={category}>
                    <p className="text-[10px] text-text-secondary font-medium uppercase tracking-wide mb-1.5">{formatCategory(category)}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {keys.map((key) => (
                        <span key={key} className="text-[12px] px-2.5 py-1 rounded-lg border border-border-primary bg-background-icon/20 text-text-primary">
                          {formatLabel(key)}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </Section>
      )}

      {/* ── Interviewers ─────────────────────────────────────────────────── */}
      {viewJob.interviewers?.some((i) => i.name || i.email) && (
        <Section title="Interviewers">
          <div className="flex flex-wrap gap-2">
            {viewJob.interviewers.map(
              (person) =>
                (person.name || person.email) && (
                  <div key={person.email} className={tileBase}>
                    <UserRound size={13} className="text-text-secondary flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[13px] font-medium text-text-primary leading-tight break-words">{person.name || "—"}</p>
                      {person.email && (
                        <a href={`mailto:${person.email}`} className="text-[11px] text-text-secondary hover:text-text-primary transition-colors truncate block mt-0.5">
                          {person.email}
                        </a>
                      )}
                    </div>
                  </div>
                ),
            )}
          </div>
        </Section>
      )}


      {/* ── Company Links ────────────────────────────────────────────────── */}
      {(hasValue(viewJob.company_link) || hasValue(viewJob.company_linkedin_link)) && (
        <Section title="Company Links">
          <div className="flex flex-wrap gap-2">
            {viewJob.company_link && (
              <a href={viewJob.company_link} target="_blank" rel="noopener noreferrer" className={`${tileBase} hover:border-border-primary transition-colors`}>
                <Globe size={13} className="text-text-secondary flex-shrink-0" />
                <span className="text-[12px] text-text-primary truncate">{viewJob.company_link}</span>
              </a>
            )}
            {viewJob.company_linkedin_link && (
              <a href={viewJob.company_linkedin_link} target="_blank" rel="noopener noreferrer" className={`${tileBase} hover:border-border-primary transition-colors`}>
                <Globe size={13} className="text-text-secondary flex-shrink-0" />
                <span className="text-[12px] text-text-primary truncate">LinkedIn</span>
              </a>
            )}
          </div>
        </Section>
      )}

      {/* ── Consent Confirmation Emails ──────────────────────────────────── */}
      <ConsentSection viewJob={viewJob} />
    </div>
  );
};

/**
 * What each email-consent flow does on this job, resolved.
 *
 * The labels come from `emailConsentFlows` on the tenant-settings response, so
 * the three strings live on the server and not a second time here. That is this
 * section's own fetch: it keeps its own spinner and its own retry, and a failure
 * takes nothing else on the page with it.
 *
 * The value renders as words. `hasValue` above drops anything falsy, so a
 * resolved `false` passed through raw would silently remove the row — and the
 * row saying "Not required" is exactly the case that matters most.
 *
 * The suffix is a three-way pick, and enforcement comes first. A flow the org
 * enforces has no answer of its own to fix, so "following organization
 * settings" — which tells the reader to go and save the job — would send them
 * to a row the job form draws disabled.
 */
const ConsentSection = ({ viewJob }) => {
  const [flows, setFlows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await TenantSettingsService.getSettings();
      setFlows(response?.data?.emailConsentFlows ?? []);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to load consent settings");
      setFlows(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Section title="Consent Confirmation Emails">
        <div className="flex justify-center py-6" role="status" aria-label="Loading consent settings">
          <Loader2 className="h-6 w-6 animate-spin text-text-primary" />
        </div>
      </Section>
    );
  }

  if (error) {
    return (
      <Section title="Consent Confirmation Emails">
        <div className="py-4 text-center">
          <p className="mb-3 text-[13px] text-tone-rose">{error}</p>
          <RetryButton onClick={load} />
        </div>
      </Section>
    );
  }

  if (!flows?.length) return null;

  return (
    <Section title="Consent Confirmation Emails">
      <div className="flex flex-col gap-2">
        {flows.map((flow) => {
          const tile = (
            <div key={flow.column} className={tileBase}>
              <MailCheck size={13} className="text-text-secondary flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-[13px] font-medium text-text-primary leading-tight break-words">{flow.label}</p>
                <p className="mt-0.5 text-[11px] text-text-secondary">
                  <span className="text-text-primary">{viewJob[flow.column] ? "Required" : "Not required"}</span>
                  {viewJob[`${flow.column}_enforced`]
                    ? " — set by your organization"
                    : viewJob[`${flow.column}_inherited`]
                      ? " — following organization settings"
                      : ""}
                </p>
              </div>
            </div>
          );

          // Same rule the job form and the org settings page follow: a row
          // about a flow the tenant does not own explains nothing and invites a
          // support ticket. `w-full`: the tile spans the section, so the
          // wrapper has to as well.
          return flow.module ? (
            <ModuleGate key={flow.column} module={flow.module} className="w-full">
              {tile}
            </ModuleGate>
          ) : (
            tile
          );
        })}
      </div>
    </Section>
  );
};

export default JobDetailsBody;
